import React, { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Cropper from 'react-easy-crop';
import { 
  Menu, X, Shield, BookOpen, MapPin, TrendingDown, 
  Facebook, Instagram, Calendar, Newspaper, ChevronRight,
  Mail, Phone, MapPinned, ExternalLink, Play, Quote, Award, User,
  Send, CheckCircle, Users, Download, Camera, ChevronLeft, Image, Building2,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, AlignLeft, AlignCenter, Link2,
  Crop, RotateCw, Folder, Copy, Trash2, Upload, Globe, ChevronDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sanitize HTML to prevent XSS attacks
const sanitizeHTML = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style']
  });
};

// Safe HTML renderer component to avoid inline object recreation
const SafeHTML = ({ html, className }) => {
  const sanitizedHTML = React.useMemo(() => sanitizeHTML(html), [html]);
  return <div className={className} dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
};

// Helper to resolve image URLs (handles both uploaded and external images)
const resolveImageUrl = (url) => {
  if (!url) return null;
  
  // Handle full URLs from old uploads - extract just the path
  if (url.includes('preview.emergentagent.com') || url.includes('emergent.host')) {
    const match = url.match(/\/api\/uploads\/[^?#]+/);
    if (match) {
      return `${BACKEND_URL}${match[0]}`;
    }
  }
  
  // Handle /api/uploads/ paths (new format)
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  // Handle /uploads/ paths (old format) - convert to /api/uploads/
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return url;
};

// Icon mapping for dynamic icons from database
const ICON_MAP = {
  Shield: Shield,
  BookOpen: BookOpen,
  MapPin: MapPin,
  TrendingDown: TrendingDown,
  Award: Award,
  User: User,
  Users: Users,
  Calendar: Calendar,
  Building2: Building2
};

// Rich Text Editor Component using TipTap
const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({
        openOnClick: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
          title="Bold"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
          title="Italic"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
          title="Underline"
        >
          <UnderlineIcon size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
          title="Heading"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
          title="Subheading"
        >
          H3
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
          title="Bullet List"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
          title="Numbered List"
        >
          <ListOrdered size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
          title="Align Left"
        >
          <AlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
          title="Align Center"
        >
          <AlignCenter size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={setLink}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
          title="Add Link"
        >
          <Link2 size={18} />
        </button>
      </div>
      
      {/* Editor */}
      <EditorContent editor={editor} className="prose max-w-none p-4 min-h-[200px] focus:outline-none" />
    </div>
  );
};

// Helper to create cropped image
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = new window.Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

// Image Cropper Component
const ImageCropperModal = ({ imageSrc, onCropComplete, onCancel, aspectRatio = 1 }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedAspect, setSelectedAspect] = useState(aspectRatio);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (croppedAreaPixels) {
      const croppedBlob = await createCroppedImage(imageSrc, croppedAreaPixels);
      onCropComplete(croppedBlob);
    }
  };

  const aspectOptions = [
    { label: 'Square (1:1)', value: 1 },
    { label: 'Landscape (4:3)', value: 4/3 },
    { label: 'Portrait (3:4)', value: 3/4 },
    { label: 'Wide (16:9)', value: 16/9 },
    { label: 'Free', value: null },
  ];

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-black">
        <div className="flex items-center gap-4">
          <Crop className="text-white" size={24} />
          <span className="text-white font-heading text-lg uppercase">Crop Image</span>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedAspect || 'free'}
            onChange={(e) => setSelectedAspect(e.target.value === 'free' ? null : parseFloat(e.target.value))}
            className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600"
          >
            {aspectOptions.map((opt) => (
              <option key={opt.label} value={opt.value || 'free'}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Cropper */}
      <div className="flex-1 relative">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={selectedAspect}
          onCropChange={onCropChange}
          onCropComplete={onCropCompleteCallback}
          onZoomChange={onZoomChange}
        />
      </div>

      {/* Controls */}
      <div className="bg-black px-6 py-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-4 flex-1">
            <span className="text-white text-sm">Zoom:</span>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 max-w-xs"
            />
            <span className="text-white text-sm w-12">{Math.round(zoom * 100)}%</span>
          </div>
          <div className="flex gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-campaign-red text-white rounded hover:bg-campaign-red-hover transition-colors font-semibold"
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Page title helper
const usePageTitle = (title) => {
  useEffect(() => {
    const baseTitle = "John Isaac for NY State Assembly District 90 | Yonkers";
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }, [title]);
};

// Campaign data
const CAMPAIGN_DATA = {
  donateUrl: "https://secure.winred.com/john-isaac-for-new-york-state-assembly/donate-today",
  facebook: "https://www.facebook.com/isaacj4assembly90",
  instagram: "https://www.instagram.com/isaac4assembly90/?hl=en",
  youtube: "https://www.youtube.com/embed/GpETQFnr5Gs",
  logo: "https://customer-assets.emergentagent.com/job_e0df2d40-5539-4842-b995-55df87af5c15/artifacts/i5jezux4_LOGO-ALLWHITE.png",
  logoSvg: "https://customer-assets.emergentagent.com/job_isaac-assembly-90/artifacts/ha7ov0rp_LOGO-dark.svg",
  logoWhiteSvg: "https://customer-assets.emergentagent.com/job_isaac-assembly-90/artifacts/n49f7kam_LOGO-ALLWHITE.svg",
  images: {
    heroTransparent: "/api/uploads/74ca7234-0d7d-4ddf-a822-c56f847d8d47.png",
    heroMobile: "https://customer-assets.emergentagent.com/job_isaac-assembly-90/artifacts/m4jime1q_IMG-20241007-WA0048.jpg",
    yonkersBackground: "https://customer-assets.emergentagent.com/job_isaac-assembly-90/artifacts/dnrxlvje_AdobeStock_1568034137_Editorial_Use_Only.jpeg",
    heroCrop: "https://customer-assets.emergentagent.com/job_isaac-assembly-90/artifacts/upba5wdl_photo-for-hero.png",
    yonkers: "https://customer-assets.emergentagent.com/job_isaac-assembly-90/artifacts/3i1xszn0_AdobeStock_1568034137_Editorial_Use_Only.jpeg",
    hero: "https://customer-assets.emergentagent.com/job_isaac-assembly-90/artifacts/soyluo5k_IMG_9967.jpg",
    family: "https://customer-assets.emergentagent.com/job_e0df2d40-5539-4842-b995-55df87af5c15/artifacts/arzipq8u_isaac-family.jpg",
    headshot: "https://customer-assets.emergentagent.com/job_e0df2d40-5539-4842-b995-55df87af5c15/artifacts/biovxoni_IMG-20241007-WA0048.jpg",
    campaign1: "/api/uploads/7249e869-503e-4d35-a059-c38d1de5eb6f.jpg",
    campaign2: "/api/uploads/b8640cfd-5174-42c0-b663-1007fa441120.jpg",
  },
  gallery: [
    {
      url: "/api/uploads/60940642-0085-4762-aa75-c36bda643ae0.jpg",
      caption: "John Isaac speaking at community event"
    },
    {
      url: "/api/uploads/e6bc9ce3-90b2-4e7e-ad7a-e8b03438339f.jpg",
      caption: "Campaign rally with supporters"
    },
    {
      url: "/api/uploads/2181b700-8177-4644-924b-111d88ce880a.jpg",
      caption: "John with community members"
    },
    {
      url: "/api/uploads/de3207a5-da6b-4066-bcb9-80f613294a9a.jpg",
      caption: "Meeting local residents"
    },
    {
      url: "/api/uploads/b8265888-e7ff-4277-8fcc-ea5cd1331796.jpg",
      caption: "Family at campaign event"
    },
    {
      url: "/api/uploads/2e601a0c-950b-4254-a972-0c8aff8c1b14.jpg",
      caption: "Community outreach"
    },
    {
      url: "/api/uploads/f9a151ab-bad8-46a9-8e1d-d2261bc2aa92.jpg",
      caption: "Campaign event with supporters"
    },
    {
      url: "/api/uploads/96615b41-f98f-4cea-ada4-cd5c69671d87.jpg",
      caption: "John with fellow Republicans"
    },
    {
      url: "/api/uploads/e39e490b-de30-47b7-a833-df25f7689440.jpg",
      caption: "Summer campaign gathering"
    },
  ],
  tagline: "Businessman. Immigrant. Yonkers Resident.",
  slogan: "Everyone Wins with Isaac",
  subSlogan: "End the Status Quo. Restore Common Sense.",
  bio: `John Isaac is a local businessman, financial advisor, legal immigrant, and proud resident of Yonkers. He is running for New York State Assembly in the 90th District — the district that represents the City of Yonkers.

John believes public office is a public trust — not a political inheritance. For too long, Albany insiders have treated leadership like it belongs to them. John is running to return that power to the people of Yonkers.`,
  bioShort: `As a legal immigrant, John understands hard work, responsibility, and opportunity. He came to this country to build a better life — and he raised his family right here in Yonkers. In 2024, John earned 44% of the vote. This time, with your support, he's ready to finish the job.`,
  bioExtended: `John immigrated from India at 19 and built a successful career in finance, working at Merrill Lynch and MetLife. He is now a vice president at Forest Hills Financial Group. A Yonkers resident for more than 16 years, he lives with his wife, Shelby, and is the proud father of Alisha & Josh, and grateful "son-in-love" Krishna Patel.`,
  contact: {
    website: "votejohnisaac.com",
    address: "PO Box 565 Yonkers, NY 10704",
    paidFor: "Friends of John Isaac"
  },
  issues: [
    { 
      title: "Make Yonkers Affordable", 
      icon: TrendingDown, 
      desc: "Lower taxes. Eliminate the Yonkers income tax. Bring real budget discipline to Albany.",
      details: [
        "Lower taxes statewide",
        "Eliminate the Yonkers personal income tax",
        "Enforce real budget discipline",
        "Stop policies that drive working families out"
      ]
    },
    { 
      title: "Fix Public Safety", 
      icon: Shield, 
      desc: "Reform failed bail laws. Stop releasing repeat violent offenders. Fully support law enforcement.",
      details: [
        "Fix broken bail laws",
        "Support law enforcement",
        "Put victims before criminals",
        "Restore accountability"
      ]
    },
    { 
      title: "Fair Funding for Schools", 
      icon: BookOpen, 
      desc: "Fix the broken school funding formula. Ensure Yonkers gets its fair share. Prepare students for real opportunity.",
      details: [
        "Fight for fair school funding",
        "Ensure accountability in education spending",
        "Expand real opportunity for students"
      ]
    },
    { 
      title: "Jobs & Economic Growth", 
      icon: MapPin, 
      desc: "Support full casino gaming in Yonkers. Keep jobs and revenue local. Deliver relief to taxpayers.",
      details: [
        "Create jobs",
        "Increase local revenue",
        "Deliver tax relief to residents"
      ]
    },
  ]
};

// Language Selector Component
const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('English');
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'ur', name: 'Urdu', native: 'اردو' },
    { code: 'ar', name: 'Arabic', native: 'العربية' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const translatePage = (langCode) => {
    // Use Google Translate
    const googleTranslateSelect = document.querySelector('.goog-te-combo');
    if (googleTranslateSelect) {
      googleTranslateSelect.value = langCode;
      googleTranslateSelect.dispatchEvent(new Event('change'));
    } else {
      // Fallback: open Google Translate in new tab
      const currentUrl = window.location.href;
      window.open(`https://translate.google.com/translate?sl=en&tl=${langCode}&u=${encodeURIComponent(currentUrl)}`, '_blank');
    }
    const lang = languages.find(l => l.code === langCode);
    setCurrentLang(lang?.native || 'English');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-all"
        data-testid="language-selector"
      >
        <Globe size={16} />
        <span className="hidden sm:inline">{currentLang}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => translatePage(lang.code)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-800 flex justify-between items-center"
              data-testid={`lang-${lang.code}`}
            >
              <span>{lang.name}</span>
              <span className="text-gray-500 text-sm">{lang.native}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav data-testid="navbar" className={`${scrolled ? 'fixed top-0 left-0 right-0 shadow-lg' : 'relative'} z-50 transition-all duration-300 bg-navy`}>
      <div className="container-campaign">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src={CAMPAIGN_DATA.logoWhiteSvg} 
              alt="John Isaac for Assembly" 
              className="h-24 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-home">Home</Link>
            <Link to="/about" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-about">About</Link>
            <a href="/#early-voting" className="text-campaign-red hover:text-red-400 text-lg font-black transition-colors duration-200 flex items-center gap-1" data-testid="nav-early-voting">
              🗳️ Early Voting
            </a>
            <Link to="/events" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-events">Events</Link>
            <Link to="/news" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-news">News</Link>
            <LanguageSelector />
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-donate text-lg"
              data-testid="nav-donate-btn"
            >
              Donate
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mobile-menu-enter pb-4" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Home</Link>
              <Link to="/about" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>About</Link>
              <a href="/#early-voting" className="text-campaign-red hover:text-red-400 py-2 font-black flex items-center gap-1" onClick={() => setIsOpen(false)}>
                🗳️ Early Voting
              </a>
              <Link to="/events" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Events</Link>
              <Link to="/news" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>News</Link>
              <div className="py-2">
                <LanguageSelector />
              </div>
              <a 
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-donate text-center"
              >
                Donate
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-navy text-white py-16">
      <div className="container-campaign">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Tagline */}
          <div className="col-span-1 md:col-span-2">
            <img 
              src={CAMPAIGN_DATA.logoWhiteSvg} 
              alt="John Isaac for Assembly" 
              className="h-40 w-auto mb-4"
            />
            <p className="text-gray-300 mb-2 font-black">
              {CAMPAIGN_DATA.slogan}
            </p>
            <p className="text-gray-400 mb-4 font-body">
              {CAMPAIGN_DATA.tagline}
            </p>
            <div className="flex space-x-4">
              <a href={CAMPAIGN_DATA.facebook} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-facebook">
                <Facebook size={20} />
              </a>
              <a href={CAMPAIGN_DATA.instagram} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-instagram">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors font-semibold">Home</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors font-semibold">About John</Link></li>
              <li><Link to="/events" className="text-gray-300 hover:text-white transition-colors font-semibold">Events</Link></li>
              <li><Link to="/news" className="text-gray-300 hover:text-white transition-colors font-semibold">In The News</Link></li>
              <li><Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors font-semibold">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Contact</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center gap-2">
                <MapPinned size={16} />
                <span>{CAMPAIGN_DATA.contact.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <ExternalLink size={16} />
                <span>{CAMPAIGN_DATA.contact.website}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p className="font-semibold">Paid for by {CAMPAIGN_DATA.contact.paidFor}</p>
          <p className="mt-2">Vote on November 3, 2026</p>
        </div>
        
        <div className="mt-6 text-center text-gray-500 text-xs">
          <p>Powered by <a href="https://newlogical.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">New Logical</a></p>
        </div>
      </div>
    </footer>
  );
};

// Hero Section
const HeroSection = ({ featuredEvent }) => {
  return (
    <section data-testid="hero-section" className="relative lg:min-h-[calc(100vh-7rem)] overflow-hidden">
      {/* Background - Yonkers image with dark navy overlay (desktop only) */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden lg:block"
        style={{ backgroundImage: `url(${CAMPAIGN_DATA.images.yonkersBackground})` }}
      />
      {/* Dark navy overlay - desktop only */}
      <div className="absolute inset-0 bg-navy/85 hidden lg:block" />
      
      {/* Mobile/Tablet white background */}
      <div className="absolute inset-0 bg-white lg:hidden" />
      
      {/* Content Container */}
      <div className="relative z-10 lg:min-h-[calc(100vh-7rem)] flex items-center">
        {/* John's photo on left - desktop only (transparent version) */}
        <div className="hidden lg:block absolute left-0 bottom-0 h-full z-20">
          <img 
            src={CAMPAIGN_DATA.images.heroTransparent} 
            alt="John Isaac" 
            className="h-full w-auto object-contain object-left-bottom"
            data-testid="hero-headshot"
          />
        </div>
        
        {/* Text content */}
        <div className="container-campaign w-full">
          <div className="lg:w-1/2 lg:ml-auto lg:pr-8 py-4 lg:py-0 text-center lg:text-left">
            {/* Mobile/Tablet image - full width, no gaps */}
            <div className="lg:hidden mb-4 w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-4 bg-gradient-to-b from-gray-100 to-white">
              <img 
                src="/api/uploads/74ca7234-0d7d-4ddf-a822-c56f847d8d47.png"
                alt="John Isaac"
                className="w-full h-auto max-h-[400px] object-contain mx-auto"
              />
            </div>
            
            {/* Logo - only show on desktop (mobile already has it in header) */}
            <img 
              src={CAMPAIGN_DATA.logoWhiteSvg} 
              alt="John Isaac for NY State Assembly" 
              className="h-[380px] w-auto mb-12 hidden lg:block"
              data-testid="hero-logo"
            />
            
            {/* District text - dark on mobile, white on desktop */}
            <p className="font-heading text-lg md:text-2xl lg:text-3xl text-navy lg:text-white/90 uppercase tracking-wider mb-3 mt-4 lg:mt-0">
              District 90 | Yonkers
            </p>
            
            {/* Slogan - dark on mobile, white on desktop */}
            <p className="text-xl md:text-3xl lg:text-5xl text-navy lg:text-white font-black tracking-wide mb-10">
              <span className="lg:hidden">Everyone Wins with Isaac</span>
              <span className="hidden lg:block">Everyone Wins<br />with Isaac</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 lg:gap-5 justify-center lg:justify-start px-4 lg:px-0 flex-wrap pb-8 lg:pb-12">
              <a 
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-campaign-red text-white px-8 py-4 lg:px-10 lg:py-5 text-lg lg:text-xl font-heading uppercase tracking-wide hover:bg-campaign-red-hover transition-colors text-center"
                data-testid="hero-donate-btn"
              >
                Donate Now
              </a>
              {/* Meet John button - navy border on mobile, white on desktop */}
              <Link to="/about" className="border-2 border-navy text-navy px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-navy hover:text-white transition-colors lg:hidden text-center">
                Meet John
              </Link>
              <Link to="/about" className="border-2 border-white text-white px-10 py-5 text-xl font-heading uppercase tracking-wide hover:bg-white hover:text-navy transition-colors hidden lg:block text-center">
                Meet John
              </Link>
              {/* Featured Event Button */}
              {featuredEvent && (
                <>
                  <Link 
                    to={`/events/${featuredEvent.id}`}
                    className="bg-white text-navy px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-gray-100 transition-colors lg:hidden text-center border-2 border-navy"
                    data-testid="hero-featured-event-btn"
                  >
                    {featuredEvent.featured_cta_text || `RSVP: ${featuredEvent.title}`}
                  </Link>
                  <Link 
                    to={`/events/${featuredEvent.id}`}
                    className="bg-white text-navy px-10 py-5 text-xl font-heading uppercase tracking-wide hover:bg-gray-100 transition-colors hidden lg:block text-center"
                    data-testid="hero-featured-event-btn-desktop"
                  >
                    {featuredEvent.featured_cta_text || `RSVP: ${featuredEvent.title}`}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Early Voting Banner & Section
const EarlyVotingSection = () => {
  const earlyVotingLocations = [
    {
      name: "Grinton I. Will Library",
      address: "1500 Central Park Avenue, Yonkers, NY 10710"
    },
    {
      name: "Nodine Hill Community Center", 
      address: "140 Fillmore Street, Yonkers, NY 10701"
    },
    {
      name: "Riverfront Library",
      address: "One Larkin Center, Yonkers, NY 10701"
    }
  ];

  const votingSchedule = [
    { day: "Sat, Oct 24", hours: "10 AM – 6 PM" },
    { day: "Sun, Oct 25", hours: "10 AM – 6 PM" },
    { day: "Mon, Oct 26", hours: "8 AM – 4 PM" },
    { day: "Tue, Oct 27", hours: "12 PM – 8 PM" },
    { day: "Wed, Oct 28", hours: "8 AM – 4 PM" },
    { day: "Thu, Oct 29", hours: "12 PM – 8 PM" },
    { day: "Fri, Oct 30", hours: "8 AM – 4 PM" },
    { day: "Sat, Oct 31", hours: "10 AM – 6 PM" },
    { day: "Sun, Nov 1", hours: "10 AM – 6 PM" }
  ];

  return (
    <>
      {/* Sticky Alert Banner */}
      <div className="bg-campaign-red text-white py-3">
        <div className="container-campaign">
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-center">
            <span className="font-heading text-lg md:text-xl uppercase tracking-wide animate-pulse">
              🗳️ Early Voting: Oct 24 – Nov 1
            </span>
            <a 
              href="#early-voting" 
              className="bg-white text-campaign-red px-4 py-1 rounded font-bold text-sm hover:bg-gray-100 transition-colors"
            >
              View Locations & Times
            </a>
          </div>
        </div>
      </div>

      {/* Full Early Voting Section */}
      <section id="early-voting" className="py-16 bg-navy text-white">
        <div className="container-campaign">
          <div className="text-center mb-12">
            <p className="text-campaign-red font-black uppercase tracking-wider mb-2">Make Your Voice Heard</p>
            <h2 className="font-heading text-4xl md:text-5xl uppercase mb-4">Vote Early in Yonkers</h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Don't wait until Election Day! Early voting runs <strong>October 24 – November 1</strong>. 
              Find a location near you and cast your vote for John Isaac.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Locations */}
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="font-heading text-2xl uppercase mb-6 flex items-center gap-2">
                <MapPin className="text-campaign-red" /> Voting Locations
              </h3>
              <div className="space-y-4">
                {earlyVotingLocations.map((loc, idx) => (
                  <div key={idx} className="border-b border-white/20 pb-4 last:border-0">
                    <p className="font-bold text-lg">{loc.name}</p>
                    <p className="text-white/70">{loc.address}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Schedule */}
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="font-heading text-2xl uppercase mb-6 flex items-center gap-2">
                <Calendar className="text-campaign-red" /> Voting Hours
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {votingSchedule.map((item, idx) => (
                  <div key={idx} className="flex justify-between bg-white/5 px-3 py-2 rounded">
                    <span className="font-semibold">{item.day}</span>
                    <span className="text-white/70">{item.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://customer-assets-0z36b82j.emergentagent.net/job_75970bd6-4a8c-4021-a457-d5aaa56dbc66/artifacts/7f11f40a7a1f0ec1_EARLY%20Voting%20REV%20as%20of%20Oct%2020.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-campaign-red text-white px-8 py-4 font-heading uppercase tracking-wide hover:bg-campaign-red-hover transition-colors flex items-center gap-2"
            >
              <Download size={20} /> Download Early Voting Guide
            </a>
            <a 
              href="https://ballotapplication.elections.ny.gov/home/absentee"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-8 py-4 font-heading uppercase tracking-wide hover:bg-white hover:text-navy transition-colors flex items-center gap-2"
            >
              <Mail size={20} /> Request Mail-In Ballot
            </a>
            <a 
              href="https://customer-assets-0z36b82j.emergentagent.net/job_75970bd6-4a8c-4021-a457-d5aaa56dbc66/artifacts/dafba055cca84b36_John_Isaac_Absentee_Ballot_Early_Voting.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-8 py-4 font-heading uppercase tracking-wide hover:bg-white hover:text-navy transition-colors flex items-center gap-2"
            >
              <Download size={20} /> Mail-In Ballot Info
            </a>
          </div>

          {/* Election Day Reminder */}
          <div className="mt-12 text-center">
            <p className="text-white/60">
              Can't vote early? <strong className="text-white">Election Day is November 3rd</strong>. 
              If you vote early, you cannot vote again on Election Day.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

// Meet John Section
const MeetJohnSection = () => {
  return (
    <section data-testid="meet-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-campaign-red font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
            <h2 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-6" data-testid="meet-title">
              Meet John Isaac
            </h2>
            <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bio}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bioShort}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-8">
              {CAMPAIGN_DATA.bioExtended}
            </p>
            <Link to="/about" className="btn-secondary inline-flex items-center gap-2 font-black">
              Read Full Bio <ChevronRight size={20} />
            </Link>
          </div>
          <div className="order-1 lg:order-2">
            <img 
              src="/api/uploads/a0557e56-caeb-4215-9d6f-f4e01a3a7cdf.jpg" 
              alt="John Isaac with supporters at campaign event" 
              className="w-full h-auto rounded-sm shadow-2xl"
              data-testid="meet-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Campaign Commercials Section
const CommercialsSection = () => {
  const [activeVideo, setActiveVideo] = useState(0);
  const videoRef = useRef(null);
  
  const commercials = [
    {
      title: "John Isaac for Assembly",
      url: "https://customer-assets-0z36b82j.emergentagent.net/job_75970bd6-4a8c-4021-a457-d5aaa56dbc66/artifacts/e6c9b8a9b715455d_John%20Isaac%20Commercials%201.mp4"
    },
    {
      title: "Fighting for Yonkers",
      url: "https://customer-assets-0z36b82j.emergentagent.net/job_75970bd6-4a8c-4021-a457-d5aaa56dbc66/artifacts/970cf2df134ff088_John%20Isaac%20Commercial%202.mp4"
    },
    {
      title: "Your Voice in Albany",
      url: "https://customer-assets-0z36b82j.emergentagent.net/job_75970bd6-4a8c-4021-a457-d5aaa56dbc66/artifacts/cb9ceebc73ce4e9a_Commercial%203.mp4"
    }
  ];

  const handleVideoChange = (index) => {
    setActiveVideo(index);
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  return (
    <section className="py-20 bg-gray-100">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <p className="text-campaign-red font-black uppercase tracking-wider mb-2">Watch Our Message</p>
          <h2 className="font-heading text-4xl md:text-5xl text-navy uppercase">Campaign Commercials</h2>
        </div>
        
        <div className="max-w-4xl mx-auto">
          {/* Main Video Player */}
          <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl mb-6">
            <video 
              ref={videoRef}
              controls 
              className="w-full aspect-video"
              poster=""
              preload="metadata"
            >
              <source src={commercials[activeVideo].url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
          {/* Video Title */}
          <h3 className="text-center text-xl font-heading text-navy uppercase mb-6">
            {commercials[activeVideo].title}
          </h3>
          
          {/* Video Thumbnails/Selector */}
          <div className="flex justify-center gap-4 flex-wrap">
            {commercials.map((video, index) => (
              <button
                key={index}
                onClick={() => handleVideoChange(index)}
                className={`px-6 py-3 rounded-lg font-heading uppercase text-sm transition-all ${
                  activeVideo === index 
                    ? 'bg-campaign-red text-white shadow-lg' 
                    : 'bg-white text-navy hover:bg-gray-200 border border-gray-300'
                }`}
              >
                <Play size={16} className="inline mr-2" />
                Commercial {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Issues Section
const IssuesSection = ({ issues }) => {
  // Use provided issues from API or fall back to static data
  const displayIssues = issues && issues.length > 0 ? issues : CAMPAIGN_DATA.issues;
  
  return (
    <section data-testid="issues-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <p className="text-campaign-red font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.subSlogan}</p>
          <h2 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-4" data-testid="issues-title">
            {CAMPAIGN_DATA.slogan}
          </h2>
          <p className="text-lg text-gray-600 font-body max-w-2xl mx-auto">
            Albany works for insiders. John Isaac will work for YOU.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {displayIssues.map((issue, index) => {
            // Handle both static icons (components) and dynamic icons (strings)
            const IconComponent = typeof issue.icon === 'string' ? (ICON_MAP[issue.icon] || Shield) : issue.icon;
            return (
              <div 
                key={issue.id || index} 
                className="bg-white p-8 rounded-sm shadow-sm card-hover text-center"
                data-testid={`issue-card-${index}`}
              >
                <div className="issue-icon">
                  <IconComponent className="w-8 h-8 text-navy" />
                </div>
                <h3 className="font-heading text-xl uppercase text-navy mb-3">{issue.title}</h3>
                <p className="text-gray-600 font-body text-sm">{issue.description || issue.desc}</p>
              </div>
            );
          })}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-xl font-black text-navy">Together, we will finish the job.</p>
        </div>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => {
  return (
    <section data-testid="video-section" className="py-20 bg-navy">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-4" data-testid="video-title">
            Announcement Speech
          </h2>
          <p className="text-lg text-gray-300 font-body">
            Watch John Isaac's official campaign announcement
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="video-container rounded-sm overflow-hidden shadow-2xl">
            <iframe 
              src={CAMPAIGN_DATA.youtube}
              title="John Isaac Announcement Speech"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-video"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Photo Gallery Section
const GallerySection = ({ photos }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showSlideshow, setShowSlideshow] = useState(false);
  
  // Use provided photos or fall back to static gallery
  const galleryPhotos = photos && photos.length > 0 ? photos : CAMPAIGN_DATA.gallery;
  
  // Show only first 18 photos (6 rows x 3 columns) in the grid
  const maxVisible = 18;
  const visiblePhotos = galleryPhotos.slice(0, maxVisible);
  const hasMore = galleryPhotos.length > maxVisible;
  
  const openSlideshow = (index) => {
    setSelectedIndex(index);
    setShowSlideshow(true);
  };
  
  const closeSlideshow = () => {
    setShowSlideshow(false);
    setSelectedIndex(null);
  };
  
  const goToPrev = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? galleryPhotos.length - 1 : prev - 1));
  };
  
  const goToNext = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === galleryPhotos.length - 1 ? 0 : prev + 1));
  };
  
  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSlideshow) return;
      if (e.key === 'ArrowLeft') goToPrev(e);
      if (e.key === 'ArrowRight') goToNext(e);
      if (e.key === 'Escape') closeSlideshow();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSlideshow]);

  return (
    <section data-testid="gallery-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-4" data-testid="gallery-title">
            On The Campaign Trail
          </h2>
          <p className="text-lg text-gray-600 font-body">
            John Isaac meeting with voters and community leaders across Yonkers
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {visiblePhotos.map((photo, index) => (
            <div 
              key={photo.id || index}
              className="relative aspect-square overflow-hidden rounded-sm cursor-pointer group bg-gray-200"
              onClick={() => openSlideshow(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={resolveImageUrl(photo.url)} 
                alt={photo.caption || 'Campaign photo'}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                  // Create placeholder element safely using DOM methods (no innerHTML)
                  const placeholder = document.createElement('div');
                  placeholder.className = 'text-gray-400 text-center p-4';
                  
                  // Create SVG element safely
                  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                  svg.setAttribute('class', 'w-12 h-12 mx-auto mb-2');
                  svg.setAttribute('fill', 'none');
                  svg.setAttribute('stroke', 'currentColor');
                  svg.setAttribute('viewBox', '0 0 24 24');
                  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                  path.setAttribute('stroke-linecap', 'round');
                  path.setAttribute('stroke-linejoin', 'round');
                  path.setAttribute('stroke-width', '2');
                  path.setAttribute('d', 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z');
                  svg.appendChild(path);
                  
                  // Create text span safely
                  const span = document.createElement('span');
                  span.className = 'text-xs';
                  span.textContent = 'Image unavailable';
                  
                  placeholder.appendChild(svg);
                  placeholder.appendChild(span);
                  e.target.parentElement.appendChild(placeholder);
                }}
              />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-colors duration-300 flex items-center justify-center">
                <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          ))}
        </div>
        
        {/* See More Button */}
        {hasMore && (
          <div className="text-center mt-8">
            <button
              onClick={() => openSlideshow(0)}
              className="bg-navy text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-navy-light transition-colors inline-flex items-center gap-2"
              data-testid="gallery-see-more"
            >
              <Camera size={20} />
              See All {galleryPhotos.length} Photos
            </button>
          </div>
        )}
      </div>
      
      {/* Full-screen Slideshow */}
      {showSlideshow && selectedIndex !== null && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onClick={closeSlideshow}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-black/50">
            <span className="text-white font-heading">
              {selectedIndex + 1} / {galleryPhotos.length}
            </span>
            <button 
              className="text-white hover:text-gray-300 transition-colors"
              onClick={closeSlideshow}
              data-testid="slideshow-close"
            >
              <X size={32} />
            </button>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center relative px-4" onClick={(e) => e.stopPropagation()}>
            {/* Previous Button */}
            <button
              onClick={goToPrev}
              className="absolute left-4 md:left-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-prev"
            >
              <ChevronLeft size={32} />
            </button>
            
            {/* Image */}
            <div className="max-w-5xl max-h-[75vh] flex items-center justify-center">
              <img 
                src={resolveImageUrl(galleryPhotos[selectedIndex].url)} 
                alt={galleryPhotos[selectedIndex].caption || 'Campaign photo'}
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
            
            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-next"
            >
              <ChevronRight size={32} />
            </button>
          </div>
          
          {/* Caption */}
          <div className="px-6 py-4 bg-black/50 text-center">
            <p className="text-white font-body">
              {galleryPhotos[selectedIndex].caption || 'On the campaign trail'}
            </p>
          </div>
          
          {/* Thumbnail Strip */}
          <div className="bg-black/80 px-4 py-3 overflow-x-auto">
            <div className="flex gap-2 justify-center min-w-max">
              {galleryPhotos.map((photo, index) => (
                <button
                  key={photo.id || index}
                  onClick={(e) => { e.stopPropagation(); setSelectedIndex(index); }}
                  className={`w-16 h-16 rounded overflow-hidden flex-shrink-0 transition-all ${
                    index === selectedIndex ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  <img 
                    src={resolveImageUrl(photo.url)} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Events Section
const EventsSection = ({ events, showAll = false }) => {
  const displayEvents = showAll ? events : events.slice(0, 3);
  
  return (
    <section data-testid="events-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-4" data-testid="events-title">
            Upcoming Events
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Join us on the campaign trail
          </p>
        </div>
        
        {displayEvents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-sm">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No upcoming events at this time. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayEvents.map((event, index) => (
              <div 
                key={event.id || index} 
                className="bg-gray-50 rounded-sm overflow-hidden card-hover"
                data-testid={`event-card-${index}`}
              >
                {/* Event Flyer/Image - show full image */}
                {event.image_url && (
                  <div className="relative bg-white">
                    <img 
                      src={resolveImageUrl(event.image_url)} 
                      alt={event.title}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-campaign-red font-semibold mb-2">
                    <Calendar size={18} />
                    <span>{event.date}</span>
                    {event.time && <span>| {event.time}</span>}
                  </div>
                  <h3 className="font-heading text-xl text-navy uppercase mb-2">{event.title}</h3>
                  <p className="text-gray-600 font-body mb-4">{event.description}</p>
                  {event.location && (
                    <p className="text-gray-500 text-sm mb-4 flex items-center gap-1">
                      <MapPin size={14} /> {event.location}
                    </p>
                  )}
                  <a 
                    href={event.rsvp_link || "#"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-navy text-white font-bold uppercase tracking-wider py-2 px-6 text-sm hover:bg-navy-light transition-colors"
                  >
                    RSVP
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!showAll && events.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/events" className="btn-secondary inline-flex items-center gap-2">
              View All Events <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// News Section
const NewsSection = ({ news, showAll = false }) => {
  const displayNews = showAll ? news : news.slice(0, 3);
  
  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };
  
  return (
    <section data-testid="news-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-4" data-testid="news-title">
            In The News
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Latest coverage and updates from the campaign
          </p>
        </div>
        
        {displayNews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-sm">
            <Newspaper className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No news articles yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayNews.map((article, index) => {
              const isOriginal = article.content_type === 'original';
              const videoId = getYouTubeId(article.video_url);
              
              return (
                <div 
                  key={article.id || index} 
                  className="bg-white rounded-sm overflow-hidden shadow-sm card-hover"
                  data-testid={`news-card-${index}`}
                >
                  {/* Video thumbnail or image */}
                  {videoId ? (
                    <Link 
                      to={`/news/${article.id}`}
                      className="relative w-full h-48 bg-black cursor-pointer group block"
                    >
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
                        alt={article.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                        <div className="w-16 h-16 bg-campaign-red rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-red text-white text-xs px-2 py-1 font-semibold uppercase">
                          Video
                        </span>
                      )}
                    </Link>
                  ) : article.image_url ? (
                    <div className="relative">
                      <img src={resolveImageUrl(article.image_url)} alt={article.title} className="w-full h-48 object-cover" />
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-red text-white text-xs px-2 py-1 font-semibold uppercase">
                          Campaign Update
                        </span>
                      )}
                    </div>
                  ) : isOriginal && (
                    <div className="w-full h-48 bg-navy flex items-center justify-center">
                      <span className="text-white font-heading text-2xl uppercase">Campaign Update</span>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="text-sm text-gray-500 mb-2">
                      {article.published_date} {!isOriginal && article.source && `| ${article.source}`}
                    </div>
                    <h3 className="font-heading text-xl text-navy uppercase mb-2">{article.title}</h3>
                    <p className="text-gray-600 font-body mb-4">{article.summary}</p>
                    
                    {/* Gallery indicator */}
                    {article.gallery && article.gallery.length > 0 && (
                      <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                        <Camera size={14} />
                        <span>{article.gallery.length} photo{article.gallery.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                    
                    {isOriginal ? (
                      <Link 
                        to={`/news/${article.id}`}
                        className="text-campaign-red font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        {videoId ? 'Watch Video' : 'Read More'} <ChevronRight size={14} />
                      </Link>
                    ) : article.link && (
                      <a 
                        href={article.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-campaign-red font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        Read More <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!showAll && news.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/news" className="btn-secondary inline-flex items-center gap-2">
              View All News <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// Donate CTA Section
const DonateCTA = () => {
  return (
    <section data-testid="donate-cta" className="py-20 bg-campaign-red">
      <div className="container-campaign text-center">
        <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-6">
          Support the Campaign
        </h2>
        <p className="text-xl text-white/90 font-body mb-8 max-w-2xl mx-auto">
          Help John Isaac fight for Yonkers families. Every contribution makes a difference.
        </p>
        <a 
          href={CAMPAIGN_DATA.donateUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block bg-white text-campaign-red font-bold uppercase tracking-wider py-4 px-12 text-lg hover:bg-gray-100 transition-colors shadow-lg"
          data-testid="donate-cta-btn"
        >
          Donate Now
        </a>
      </div>
    </section>
  );
};

// Endorsements Section
const EndorsementsSection = ({ endorsements }) => {
  if (!endorsements || endorsements.length === 0) {
    return null;
  }
  
  const orgEndorsements = endorsements.filter(e => e.endorsement_type === 'organization');
  const individualEndorsements = endorsements.filter(e => e.endorsement_type !== 'organization');
  
  return (
    <section data-testid="endorsements-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-4" data-testid="endorsements-title">
            Endorsements
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Leaders and organizations who support John Isaac for Assembly
          </p>
        </div>
        
        {/* Organization Endorsements */}
        {orgEndorsements.length > 0 && (
          <div className="mb-16">
            <h3 className="font-heading text-2xl text-navy uppercase mb-8 text-center">Endorsed By</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {orgEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover flex flex-col items-center text-center"
                  data-testid={`org-endorsement-${index}`}
                >
                  {endorsement.image_url ? (
                    <img 
                      src={resolveImageUrl(endorsement.image_url)} 
                      alt={endorsement.name}
                      className="h-20 w-auto object-contain mb-4"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded bg-navy/10 flex items-center justify-center mb-4">
                      <Building2 className="w-10 h-10 text-navy" />
                    </div>
                  )}
                  <h4 className="font-heading text-lg text-navy uppercase">{endorsement.name}</h4>
                  {endorsement.title && (
                    <p className="text-gray-500 text-sm mt-1">{endorsement.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Individual Endorsements */}
        {individualEndorsements.length > 0 && (
          <div>
            {orgEndorsements.length > 0 && (
              <h3 className="font-heading text-2xl text-navy uppercase mb-8 text-center">Individual Endorsements</h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {individualEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover"
                  data-testid={`endorsement-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    {endorsement.image_url ? (
                      <img 
                        src={resolveImageUrl(endorsement.image_url)} 
                        alt={endorsement.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-8 h-8 text-navy" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-heading text-lg text-navy uppercase">{endorsement.name}</h3>
                      {endorsement.title && (
                        <p className="text-campaign-red font-semibold text-sm">{endorsement.title}</p>
                      )}
                      {endorsement.organization && (
                        <p className="text-gray-500 text-sm">{endorsement.organization}</p>
                      )}
                    </div>
                  </div>
                  {endorsement.quote && (
                    <div className="mt-4 relative">
                      <Quote className="w-6 h-6 text-navy/20 absolute -top-1 -left-1" />
                      <p className="text-gray-600 font-body italic pl-5">"{endorsement.quote}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Email/Text Signup Section
const SignupSection = () => {
  const [form, setForm] = useState({
    email: '',
    phone: '',
    first_name: '',
    last_name: '',
    wants_email: true,
    wants_text: false,
    text_consent: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wants_email && !form.wants_text) {
      setError('Please select at least one option (email or text)');
      return;
    }
    if (form.wants_text && !form.phone) {
      setError('Please enter your phone number to receive text updates');
      return;
    }
    if (form.wants_text && !form.text_consent) {
      setError('Please check the text message consent box to receive text updates');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/subscribe`, form);
      setSuccess(true);
      setForm({ email: '', phone: '', first_name: '', last_name: '', wants_email: true, wants_text: false, text_consent: false });
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section data-testid="signup-section" className="py-20 bg-navy">
        <div className="container-campaign text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
            You're Signed Up!
          </h2>
          <p className="text-lg text-gray-300 font-body">
            Thank you for joining our campaign. We'll keep you updated on our progress.
          </p>
          <button 
            onClick={() => setSuccess(false)}
            className="mt-6 text-white/70 hover:text-white underline"
          >
            Sign up another person
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="signup-section" className="py-20 bg-navy">
      <div className="container-campaign">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
            Stay Connected
          </h2>
          <p className="text-lg text-gray-300 font-body mb-8">
            Join our email and text list to receive campaign updates, event invitations, and ways to get involved.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="First Name"
                value={form.first_name}
                onChange={(e) => setForm({...form, first_name: e.target.value})}
                className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={form.last_name}
                onChange={(e) => setForm({...form, last_name: e.target.value})}
                className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
              />
            </div>
            
            <input
              type="email"
              placeholder="Email Address *"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
              required
            />
            
            <input
              type="tel"
              placeholder="Phone Number (optional)"
              value={form.phone}
              onChange={(e) => setForm({...form, phone: e.target.value})}
              className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
            />
            
            {/* Text Message Consent Checkbox - Always visible */}
            <div className="text-left bg-white/5 p-4 rounded-sm border border-white/10">
              <label className="flex items-start gap-3 cursor-pointer text-white">
                <input
                  type="checkbox"
                  checked={form.text_consent}
                  onChange={(e) => setForm({...form, text_consent: e.target.checked, wants_text: e.target.checked})}
                  className="w-5 h-5 rounded mt-1 flex-shrink-0"
                />
                <span className="text-sm text-gray-300 leading-relaxed">
                  By checking this box, you consent to receive text messages. Messages may include campaign updates, event information, volunteer opportunities, and requests for donation. Msg & data rates may apply. Msg frequency may vary. Reply STOP to opt-out and HELP for help. View Terms of Service and Privacy Policy for more information at <a href="https://votejohnisaac.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-campaign-red hover:underline">https://votejohnisaac.com/privacy-policy</a>
                </span>
              </label>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-white">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.wants_email}
                  onChange={(e) => setForm({...form, wants_email: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
                <span className="flex items-center gap-1">
                  <Mail size={16} /> Email Updates
                </span>
              </label>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto bg-campaign-red hover:bg-campaign-red-hover text-white font-black uppercase tracking-wider py-4 px-12 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
              data-testid="signup-submit"
            >
              {loading ? 'Signing Up...' : (
                <>
                  <Send size={18} /> Sign Up
                </>
              )}
            </button>
          </form>
          
          <p className="text-gray-400 text-sm mt-6">
            We respect your privacy. <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">View Privacy Policy</Link>
          </p>
        </div>
      </div>
    </section>
  );
};

// Home Page
const Home = () => {
  usePageTitle(null); // Use base title for home
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [issues, setIssues] = useState([]);
  const [featuredEvent, setFeaturedEvent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, newsRes, endorsementsRes, galleryRes, issuesRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/news`),
          axios.get(`${API}/endorsements`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/issues`)
        ]);
        setEvents(eventsRes.data);
        setNews(newsRes.data);
        setEndorsements(endorsementsRes.data);
        setGalleryPhotos(galleryRes.data);
        setIssues(issuesRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
        // Set placeholder data
        setEvents([{
          id: 'placeholder',
          title: 'Campaign Fundraiser',
          description: 'Join us for an evening reception to support John Isaac for Assembly.',
          date: 'TBD',
          time: 'TBD',
          location: 'Yonkers, NY',
          rsvp_link: '#'
        }]);
      }
      
      // Fetch featured event separately so it doesn't block other data
      try {
        const featuredRes = await axios.get(`${API}/events/featured/current`);
        if (featuredRes.data) {
          setFeaturedEvent(featuredRes.data);
        }
      } catch (e) {
        console.error("Error fetching featured event:", e);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection featuredEvent={featuredEvent} />
      <EarlyVotingSection />
      <MeetJohnSection />
      <CommercialsSection />
      <IssuesSection issues={issues} />
      <GallerySection photos={galleryPhotos} />
      <EndorsementsSection endorsements={endorsements} />
      <SignupSection />
      <EventsSection events={events} />
      <NewsSection news={news} />
      <DonateCTA />
    </div>
  );
};

// About Page
const AboutPage = () => {
  usePageTitle("About John");
  return (
    <div className="pt-28" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-navy">
        <div className="container-campaign">
          <p className="text-campaign-red font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">About John</h1>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-20">
        <div className="container-campaign">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl text-navy uppercase mb-6">Meet John Isaac</h2>
              <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bio}
              </p>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bioExtended}
              </p>
              
              {/* End the Status Quo */}
              <div className="bg-gray-50 p-8 rounded-sm mb-8">
                <h3 className="font-heading text-2xl text-navy uppercase mb-4">End the Status Quo in Albany</h3>
                <p className="text-gray-700 mb-4">New York families are facing:</p>
                <ul className="space-y-2 mb-4 text-gray-700">
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Higher taxes</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Higher crime</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Higher cost of living</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Families leaving the state in record numbers</li>
                </ul>
                <p className="text-gray-700 font-semibold">One-party rule has manufactured crisis after crisis. It's time for change.</p>
                <p className="text-gray-600 mt-2">John's campaign is built on accountability, common sense, and restoring balance in Albany.</p>
              </div>

              <h3 className="font-heading text-2xl text-navy uppercase mb-4">Platform</h3>
              
              {/* Make Yonkers Affordable */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-navy font-black text-lg">Make Yonkers and New York Affordable</strong>
                    <p className="text-gray-600 mt-1">New York doesn't have a revenue problem — it has a spending problem. As a financial advisor, John has spent his career helping families and businesses balance budgets and plan for the future. Albany must do the same.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Lower taxes statewide</li>
                      <li>• Eliminate the Yonkers personal income tax</li>
                      <li>• Enforce real budget discipline</li>
                      <li>• Stop policies that drive working families out</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Restore Public Safety */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <Shield className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-navy font-black text-lg">Restore Public Safety</strong>
                    <p className="text-gray-600 mt-1">Public safety must be the top priority. Failed bail reform policies have allowed repeat violent offenders back onto our streets. Our communities are paying the price.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Fix broken bail laws</li>
                      <li>• Support law enforcement</li>
                      <li>• Put victims before criminals</li>
                      <li>• Restore accountability</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Fix Our Schools */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <BookOpen className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-navy font-black text-lg">Fix Our Schools</strong>
                    <p className="text-gray-600 mt-1">Our children deserve better. The current school funding formula is broken, and Yonkers schools are not receiving their fair share. Students are falling behind, and families are frustrated.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Fight for fair school funding</li>
                      <li>• Ensure accountability in education spending</li>
                      <li>• Expand real opportunity for students</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Grow the Economy */}
              <div className="mb-8">
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-navy font-black text-lg">Grow the Economy & Create Jobs</strong>
                    <p className="text-gray-600 mt-1">Yonkers is home to one of the most important economic opportunities in the region — full casino gaming. John supports securing full casino status to:</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Create jobs</li>
                      <li>• Increase local revenue</li>
                      <li>• Deliver tax relief to residents</li>
                    </ul>
                    <p className="text-gray-600 mt-2">Albany has failed to act. John will fight to bring those benefits home.</p>
                  </div>
                </div>
              </div>
              
              {/* A Personal Commitment */}
              <div className="bg-navy text-white p-8 rounded-sm mb-8">
                <h3 className="font-heading text-2xl uppercase mb-4">A Personal Commitment</h3>
                <p className="text-gray-200 leading-relaxed">
                  {CAMPAIGN_DATA.bioShort}
                </p>
              </div>
              
              {/* Together We Win */}
              <div className="bg-campaign-red text-white p-8 rounded-sm">
                <h3 className="font-heading text-2xl uppercase mb-4">Together, We Win</h3>
                <p className="text-white/90 mb-4">This campaign is about:</p>
                <ul className="space-y-2 text-white/90 mb-4">
                  <li className="flex items-center gap-2">✓ Common sense leadership</li>
                  <li className="flex items-center gap-2">✓ Accountability in government</li>
                  <li className="flex items-center gap-2">✓ Ending one-party rule</li>
                  <li className="flex items-center gap-2">✓ Putting Yonkers families first</li>
                </ul>
                <p className="text-white font-black text-lg">It's time to restore the Empire State.</p>
              </div>
            </div>
            
            <div>
              <img 
                src={CAMPAIGN_DATA.images.headshot} 
                alt="John Isaac" 
                className="w-full rounded-sm shadow-lg mb-6"
              />
              <img 
                src={CAMPAIGN_DATA.images.family} 
                alt="John Isaac with family" 
                className="w-full rounded-sm shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>
      
      <DonateCTA />
    </div>
  );
};

// Events Page
const EventsPage = () => {
  usePageTitle("Events");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (e) {
        console.error("Error fetching events:", e);
        setEvents([{
          id: 'placeholder',
          title: 'Campaign Fundraiser',
          description: 'Join us for an evening reception to support John Isaac for Assembly.',
          date: 'TBD',
          time: 'TBD',
          location: 'Yonkers, NY',
          rsvp_link: '#'
        }]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="pt-28" data-testid="events-page">
      <section className="py-20 bg-navy">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">Events</h1>
        </div>
      </section>
      
      <EventsSection events={events} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// Event Detail Page
const EventDetailPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${API}/events/${eventId}`);
        setEvent(res.data);
      } catch (e) {
        console.error("Error fetching event:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId]);

  usePageTitle(event?.title || "Event");

  if (loading) {
    return (
      <div className="pt-28 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="pt-28 min-h-screen">
        <div className="container-campaign py-20 text-center">
          <h1 className="font-heading text-4xl text-navy uppercase mb-4">Event Not Found</h1>
          <p className="text-gray-600 mb-8">The event you're looking for doesn't exist or has been removed.</p>
          <Link to="/events" className="btn-primary">View All Events</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28" data-testid="event-detail-page">
      {/* Hero Section with Event Image */}
      <section className="py-12 bg-navy">
        <div className="container-campaign">
          <Link to="/events" className="text-white/70 hover:text-white flex items-center gap-2 mb-4">
            <ChevronLeft size={20} /> Back to Events
          </Link>
          <h1 className="font-heading text-4xl md:text-6xl text-white uppercase">{event.title}</h1>
        </div>
      </section>
      
      <section className="py-16">
        <div className="container-campaign">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Event Flyer */}
            {event.image_url && (
              <div className="bg-gray-50 p-4 rounded-sm">
                <img 
                  src={resolveImageUrl(event.image_url)} 
                  alt={event.title}
                  className="w-full h-auto rounded shadow-lg"
                />
              </div>
            )}
            
            {/* Event Details */}
            <div className={!event.image_url ? 'lg:col-span-2' : ''}>
              <div className="bg-gray-50 p-8 rounded-sm mb-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-lg">
                    <Calendar className="text-campaign-red" size={24} />
                    <div>
                      <span className="font-bold text-navy">{event.date}</span>
                      {event.time && <span className="text-gray-600"> at {event.time}</span>}
                    </div>
                  </div>
                  
                  {event.location && (
                    <div className="flex items-center gap-3 text-lg">
                      <MapPin className="text-campaign-red" size={24} />
                      <span className="text-gray-700">{event.location}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none mb-8">
                <p className="text-gray-700 text-lg leading-relaxed">{event.description}</p>
              </div>
              
              {event.rsvp_link && event.rsvp_link !== '#' && (
                <a 
                  href={event.rsvp_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-campaign-red text-white font-heading text-xl uppercase tracking-wider py-4 px-10 hover:bg-campaign-red-hover transition-colors"
                >
                  RSVP Now
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
      
      <DonateCTA />
    </div>
  );
};

// News Page
const NewsPage = () => {
  usePageTitle("In The News");
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`${API}/news`);
        setNews(res.data);
      } catch (e) {
        console.error("Error fetching news:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="pt-28" data-testid="news-page">
      <section className="py-20 bg-navy">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">In The News</h1>
        </div>
      </section>
      
      <NewsSection news={news} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// Admin Login Page
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Use POST body instead of HTTP Basic Auth to prevent browser popup
      const response = await axios.post(`${API}/admin/login`, { 
        username, 
        password 
      }, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (response.data.authenticated) {
        onLogin({ username, password });
      } else {
        setError('Invalid credentials');
      }
    } catch (e) {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="admin-login">
      <div className="bg-white p-8 rounded-sm shadow-lg w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CAMPAIGN_DATA.logoSvg} alt="Logo" className="h-20 mx-auto mb-4 logo-light" />
          <h1 className="font-heading text-2xl text-navy uppercase">Admin Login</h1>
        </div>
        
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-gray-700 font-black mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              required
              data-testid="admin-username"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 font-black mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              required
              data-testid="admin-password"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-navy text-white font-black uppercase py-3 hover:bg-navy-light transition-colors disabled:opacity-50"
            data-testid="admin-submit"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <Link to="/" className="text-gray-500 hover:text-navy font-semibold">
            &larr; Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

// Media Library Component
const MediaLibrary = ({ auth, media, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const authConfig = { auth: { username: auth.username, password: auth.password } };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        // Upload to cloud storage
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadRes = await axios.post(`${API}/admin/upload`, formData, {
          ...authConfig,
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Get file type
        const fileType = file.type.startsWith('image/') ? 'image' : 
                        file.type.startsWith('video/') ? 'video' : 'document';

        // Save to media library
        await axios.post(`${API}/admin/media`, {
          filename: file.name,
          url: uploadRes.data.url,
          file_type: fileType,
          size_bytes: file.size
        }, authConfig);
      }
      onRefresh();
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file(s)');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await axios.delete(`${API}/admin/media/${id}`, authConfig);
      onRefresh();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const copyUrl = (url) => {
    const fullUrl = url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="p-6" data-testid="media-library">
      <div className="mb-6">
        <h3 className="font-heading text-xl text-navy uppercase mb-2">Media Library</h3>
        <p className="text-gray-600 text-sm mb-4">
          Upload files here to get URLs you can use in events, news, and other content.
        </p>
        
        <label className="bg-campaign-red text-white px-6 py-3 font-semibold uppercase hover:bg-campaign-red-hover transition-colors cursor-pointer inline-flex items-center gap-2">
          <Upload size={18} />
          {uploading ? 'Uploading...' : 'Upload Files'}
          <input
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
        </label>
      </div>

      {media.length === 0 ? (
        <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <Folder className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p>No files uploaded yet. Click "Upload Files" to add media.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {media.map((file) => (
            <div key={file.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              {file.file_type === 'image' ? (
                <img 
                  src={resolveImageUrl(file.url)} 
                  alt={file.filename}
                  className="w-full h-40 object-cover bg-gray-100"
                />
              ) : (
                <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                  <Folder className="w-16 h-16 text-gray-400" />
                </div>
              )}
              <div className="p-4">
                <p className="font-semibold text-navy truncate" title={file.filename}>{file.filename}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {file.file_type} • {formatFileSize(file.size_bytes)} • {new Date(file.created_at).toLocaleDateString()}
                </p>
                
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => copyUrl(file.url)}
                    className={`flex-1 px-3 py-2 text-xs font-semibold rounded flex items-center justify-center gap-1 transition-colors ${
                      copiedId === file.url 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-navy/10 text-navy hover:bg-navy/20'
                    }`}
                  >
                    <Copy size={14} />
                    {copiedId === file.url ? 'Copied!' : 'Copy URL'}
                  </button>
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="px-3 py-2 text-xs font-semibold rounded bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center gap-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ auth, onLogout, onAuthError }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [issues, setIssues] = useState([]);
  const [media, setMedia] = useState([]);
  const [galleryDisplayMode, setGalleryDisplayMode] = useState('manual');
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const authConfig = { auth: { username: auth.username, password: auth.password } };

  // Handle API errors - if 401, trigger re-login
  const handleApiError = (error) => {
    if (error.response && error.response.status === 401) {
      // Auth failed, clear credentials and show login
      if (onAuthError) onAuthError();
    }
    console.error("API Error:", error);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, newsRes, endorsementsRes, subscribersRes, galleryRes, issuesRes, gallerySettingRes, mediaRes] = await Promise.all([
        axios.get(`${API}/admin/events`, authConfig),
        axios.get(`${API}/admin/news`, authConfig),
        axios.get(`${API}/admin/endorsements`, authConfig),
        axios.get(`${API}/admin/subscribers`, authConfig),
        axios.get(`${API}/admin/gallery`, authConfig),
        axios.get(`${API}/admin/issues`, authConfig),
        axios.get(`${API}/admin/settings/gallery_display`, authConfig),
        axios.get(`${API}/admin/media`, authConfig)
      ]);
      setEvents(eventsRes.data);
      setNews(newsRes.data);
      setEndorsements(endorsementsRes.data);
      setSubscribers(subscribersRes.data);
      setGallery(galleryRes.data);
      setIssues(issuesRes.data);
      setGalleryDisplayMode(gallerySettingRes.data.value || 'manual');
      setMedia(mediaRes.data);
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const updateGalleryDisplayMode = async (mode) => {
    try {
      await axios.put(`${API}/admin/settings/gallery_display`, { value: mode }, authConfig);
      setGalleryDisplayMode(mode);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, authConfig);
      fetchData();
    } catch (e) {
      console.error("Error deleting:", e);
    }
  };

  const handleSave = async (type, data) => {
    try {
      if (editItem) {
        await axios.put(`${API}/admin/${type}/${editItem.id}`, data, authConfig);
      } else {
        await axios.post(`${API}/admin/${type}`, data, authConfig);
      }
      fetchData();
      setShowForm(false);
      setEditItem(null);
    } catch (e) {
      handleApiError(e);
    }
  };

  const exportSubscribers = () => {
    const csv = [
      ['Email', 'First Name', 'Last Name', 'Phone', 'Wants Email', 'Wants Text', 'Text Consent', 'Subscribed At'].join(','),
      ...subscribers.map(s => [
        s.email,
        s.first_name || '',
        s.last_name || '',
        s.phone || '',
        s.wants_email ? 'Yes' : 'No',
        s.wants_text ? 'Yes' : 'No',
        s.text_consent ? 'Yes' : 'No',
        s.subscribed_at
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-navy text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img src={CAMPAIGN_DATA.logoSvg} alt="Logo" className="h-12" />
          <h1 className="font-heading text-xl uppercase">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-300 font-semibold">Welcome, {auth.username}</span>
          <button onClick={onLogout} className="bg-campaign-red px-4 py-2 text-sm font-black hover:bg-campaign-red-hover transition-colors" data-testid="admin-logout">
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="p-6">
        {/* Tabs */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => { setActiveTab('events'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'events' ? 'bg-navy text-white' : 'bg-white text-navy'}`}
            data-testid="admin-events-tab"
          >
            Events
          </button>
          <button
            onClick={() => { setActiveTab('news'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'news' ? 'bg-navy text-white' : 'bg-white text-navy'}`}
            data-testid="admin-news-tab"
          >
            News
          </button>
          <button
            onClick={() => { setActiveTab('endorsements'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'endorsements' ? 'bg-navy text-white' : 'bg-white text-navy'}`}
            data-testid="admin-endorsements-tab"
          >
            Endorsements
          </button>
          <button
            onClick={() => { setActiveTab('gallery'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'gallery' ? 'bg-navy text-white' : 'bg-white text-navy'}`}
            data-testid="admin-gallery-tab"
          >
            <Image size={18} /> Gallery ({gallery.length})
          </button>
          <button
            onClick={() => { setActiveTab('issues'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'issues' ? 'bg-navy text-white' : 'bg-white text-navy'}`}
            data-testid="admin-issues-tab"
          >
            <Shield size={18} /> Issues ({issues.length})
          </button>
          <button
            onClick={() => { setActiveTab('media'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'media' ? 'bg-navy text-white' : 'bg-white text-navy'}`}
            data-testid="admin-media-tab"
          >
            <Folder size={18} /> Media ({media.length})
          </button>
          <button
            onClick={() => { setActiveTab('subscribers'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'subscribers' ? 'bg-navy text-white' : 'bg-white text-navy'}`}
            data-testid="admin-subscribers-tab"
          >
            <Users size={18} /> Subscribers ({subscribers.length})
          </button>
        </div>

        {/* Add Button (not for subscribers or media) */}
        {activeTab !== 'subscribers' && activeTab !== 'media' && (
          <div className="mb-6">
            <button
              onClick={() => { setShowForm(true); setEditItem(null); }}
              className="bg-campaign-red text-white px-6 py-3 font-semibold uppercase hover:bg-campaign-red-hover transition-colors"
              data-testid="admin-add-btn"
            >
              Add {activeTab === 'events' ? 'Event' : activeTab === 'news' ? 'News' : activeTab === 'gallery' ? 'Photo' : activeTab === 'issues' ? 'Issue' : 'Endorsement'}
            </button>
          </div>
        )}

        {/* Export Button for subscribers */}
        {activeTab === 'subscribers' && subscribers.length > 0 && (
          <div className="mb-6">
            <button
              onClick={exportSubscribers}
              className="bg-green-600 text-white px-6 py-3 font-semibold uppercase hover:bg-green-700 transition-colors flex items-center gap-2"
              data-testid="admin-export-btn"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-sm shadow mb-6" data-testid="admin-form">
            {activeTab === 'events' ? (
              <EventForm 
                event={editItem} 
                onSave={(data) => handleSave('events', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            ) : activeTab === 'news' ? (
              <NewsForm 
                article={editItem} 
                onSave={(data) => handleSave('news', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            ) : activeTab === 'gallery' ? (
              <GalleryForm 
                photo={editItem} 
                onSave={(data) => handleSave('gallery', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
                onBulkSave={async (photos) => {
                  for (const photo of photos) {
                    await axios.post(`${API}/admin/gallery`, photo, authConfig);
                  }
                  fetchData();
                }}
              />
            ) : activeTab === 'issues' ? (
              <IssueForm 
                issue={editItem} 
                onSave={(data) => handleSave('issues', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
              />
            ) : (
              <EndorsementForm 
                endorsement={editItem} 
                onSave={(data) => handleSave('endorsements', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-sm shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : activeTab === 'events' ? (
            <table className="w-full" data-testid="admin-events-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-6 py-4">{event.title}</td>
                    <td className="px-6 py-4">{event.date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(event); setShowForm(true); }} className="text-navy hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('events', event.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No events found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'news' ? (
            <table className="w-full" data-testid="admin-news-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {news.map((article) => (
                  <tr key={article.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.content_type === 'original' ? 'bg-campaign-red/10 text-campaign-red' : 'bg-blue-100 text-blue-800'}`}>
                        {article.content_type === 'original' ? 'Original' : 'External'}
                      </span>
                      {article.video_url && <span className="ml-1 text-xs">🎬</span>}
                    </td>
                    <td className="px-6 py-4 max-w-xs truncate">{article.title}</td>
                    <td className="px-6 py-4">{article.content_type === 'original' ? 'Campaign' : (article.source || '-')}</td>
                    <td className="px-6 py-4">{article.published_date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {article.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(article); setShowForm(true); }} className="text-navy hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('news', article.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No news articles found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'endorsements' ? (
            <table className="w-full" data-testid="admin-endorsements-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Logo/Image</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {endorsements.map((endorsement) => (
                  <tr key={endorsement.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.endorsement_type === 'organization' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {endorsement.endorsement_type === 'organization' ? 'Org' : 'Individual'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{endorsement.name}</td>
                    <td className="px-6 py-4">{endorsement.title || '-'}</td>
                    <td className="px-6 py-4">
                      {endorsement.image_url ? (
                        <img src={resolveImageUrl(endorsement.image_url)} alt="" className="w-10 h-10 object-contain rounded" />
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {endorsement.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(endorsement); setShowForm(true); }} className="text-navy hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('endorsements', endorsement.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {endorsements.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No endorsements found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'gallery' ? (
            <div className="p-6">
              {/* Display Mode Selector */}
              <div className="mb-6 p-4 bg-gray-50 rounded flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-700">Display Order:</span>
                  <select
                    value={galleryDisplayMode}
                    onChange={(e) => updateGalleryDisplayMode(e.target.value)}
                    className="border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
                    data-testid="gallery-display-mode"
                  >
                    <option value="manual">Manual (by order number)</option>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="random">Random (shuffle)</option>
                  </select>
                </div>
                <p className="text-sm text-gray-500">
                  {galleryDisplayMode === 'manual' && 'Photos will display in the order you set (edit each photo to change order number)'}
                  {galleryDisplayMode === 'newest' && 'Most recently added photos will show first'}
                  {galleryDisplayMode === 'oldest' && 'Oldest photos will show first'}
                  {galleryDisplayMode === 'random' && 'Photos will be shuffled randomly on each page load'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((photo) => (
                  <div key={photo.id} className="relative group bg-gray-100 rounded overflow-hidden" data-testid={`gallery-admin-${photo.id}`}>
                    <img 
                      src={resolveImageUrl(photo.url)} 
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full aspect-square object-cover"
                    />
                    {galleryDisplayMode === 'manual' && (
                      <div className="absolute top-2 left-2 bg-navy text-white text-xs px-2 py-1 rounded font-semibold">
                        #{photo.order || 0}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      <p className="text-white text-xs text-center line-clamp-2">{photo.caption || 'No caption'}</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => { setEditItem(photo); setShowForm(true); }} 
                          className="bg-white text-navy px-3 py-1 text-xs font-semibold rounded hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete('gallery', photo.id)} 
                          className="bg-red-600 text-white px-3 py-1 text-xs font-semibold rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {gallery.length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <Image className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p>No photos in gallery. Click "Add Photo" to get started.</p>
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'issues' ? (
            <table className="w-full" data-testid="admin-issues-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Order</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Icon</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {issues.map((issue) => {
                  const IconComponent = ICON_MAP[issue.icon] || Shield;
                  return (
                    <tr key={issue.id}>
                      <td className="px-6 py-4 font-semibold">{issue.order}</td>
                      <td className="px-6 py-4">
                        <div className="w-10 h-10 rounded bg-navy/10 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-navy" />
                        </div>
                      </td>
                      <td className="px-6 py-4 font-semibold">{issue.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{issue.description}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${issue.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {issue.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => { setEditItem(issue); setShowForm(true); }} className="text-navy hover:underline mr-4">Edit</button>
                        <button onClick={() => handleDelete('issues', issue.id)} className="text-red-600 hover:underline">Delete</button>
                      </td>
                    </tr>
                  );
                })}
                {issues.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No issues found. Click "Add Issue" to create platform issues.</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'media' ? (
            <MediaLibrary auth={auth} media={media} onRefresh={fetchData} />
          ) : (
            <table className="w-full" data-testid="admin-subscribers-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Preferences</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Subscribed</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td className="px-6 py-4">{subscriber.email}</td>
                    <td className="px-6 py-4">{subscriber.first_name || subscriber.last_name ? `${subscriber.first_name || ''} ${subscriber.last_name || ''}`.trim() : '-'}</td>
                    <td className="px-6 py-4">{subscriber.phone || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {subscriber.wants_email && <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">Email</span>}
                        {subscriber.wants_text && <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Text</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(subscriber.subscribed_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleDelete('subscribers', subscriber.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {subscribers.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No subscribers yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// Event Form
const EventForm = ({ event, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    date: event?.date || 'TBD',
    time: event?.time || '',
    location: event?.location || '',
    rsvp_link: event?.rsvp_link || '#',
    image_url: event?.image_url || '',
    is_active: event?.is_active ?? true,
    is_featured: event?.is_featured ?? false,
    featured_cta_text: event?.featured_cta_text || ''
  });
  const [uploading, setUploading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, formData, {
        ...authConfig,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm({ ...form, image_url: res.data.url });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-navy uppercase mb-4">{event ? 'Edit Event' : 'Add Event'}</h3>
      
      {/* Featured Event Toggle */}
      <div className="mb-6 p-4 bg-campaign-red/10 border border-campaign-red/30 rounded">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_featured}
            onChange={(e) => setForm({...form, is_featured: e.target.checked})}
            className="w-5 h-5 accent-campaign-red"
          />
          <div>
            <span className="text-gray-800 font-bold">⭐ Feature this Event in Hero</span>
            <p className="text-sm text-gray-600">Display a button for this event on the homepage hero section</p>
          </div>
        </label>
        
        {form.is_featured && (
          <div className="mt-4">
            <label className="block text-gray-700 font-semibold mb-2">Hero Button Text</label>
            <input
              type="text"
              value={form.featured_cta_text}
              onChange={(e) => setForm({...form, featured_cta_text: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-campaign-red"
              placeholder="e.g., RSVP for Fall Fundraiser"
            />
            <p className="text-sm text-gray-500 mt-1">Leave blank to use "RSVP: [Event Title]"</p>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Date *</label>
          <input
            type="text"
            value={form.date}
            onChange={(e) => setForm({...form, date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            placeholder="e.g., TBD, January 15, 2026"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Time</label>
          <input
            type="text"
            value={form.time}
            onChange={(e) => setForm({...form, time: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            placeholder="e.g., 6:00 PM"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Location</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => setForm({...form, location: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">RSVP Link</label>
          <input
            type="text"
            value={form.rsvp_link}
            onChange={(e) => setForm({...form, rsvp_link: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            placeholder="Google Form URL or #"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Event Image</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.image_url}
              onChange={(e) => setForm({...form, image_url: e.target.value})}
              className="flex-1 border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              placeholder="Image URL or upload"
            />
            <label className="bg-navy text-white px-4 py-2 rounded cursor-pointer hover:bg-navy-light transition-colors flex items-center gap-1">
              <Upload size={16} />
              {uploading ? '...' : 'Upload'}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
          {form.image_url && (
            <img src={resolveImageUrl(form.image_url)} alt="Preview" className="mt-2 h-20 object-cover rounded" />
          )}
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
          rows="3"
          required
        />
      </div>
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-navy text-white px-6 py-2 font-semibold hover:bg-navy-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// News Form
const NewsForm = ({ article, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    title: article?.title || '',
    summary: article?.summary || '',
    content: article?.content || '',
    content_type: article?.content_type || 'external',
    source: article?.source || '',
    link: article?.link || '',
    image_url: article?.image_url || '',
    video_url: article?.video_url || '',
    gallery: article?.gallery || [],
    published_date: article?.published_date || new Date().toISOString().split('T')[0],
    is_active: article?.is_active ?? true
  });
  const [galleryUploading, setGalleryUploading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  // Extract YouTube video ID for preview
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(form.video_url);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-navy uppercase mb-4">{article ? 'Edit News' : 'Add News'}</h3>
      
      {/* Content Type Selector */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <label className="block text-gray-700 font-semibold mb-3">Post Type</label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="external"
              checked={form.content_type === 'external'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">External Link</span>
            <span className="text-gray-500 text-sm">(Link to news article)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="original"
              checked={form.content_type === 'original'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">Original Post</span>
            <span className="text-gray-500 text-sm">(Your own content/video)</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Published Date *</label>
          <input
            type="date"
            value={form.published_date}
            onChange={(e) => setForm({...form, published_date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            required
          />
        </div>
        
        {form.content_type === 'external' ? (
          <>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Source *</label>
              <input
                type="text"
                value={form.source}
                onChange={(e) => setForm({...form, source: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
                placeholder="e.g., Yonkers Tribune"
                required={form.content_type === 'external'}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Article URL *</label>
              <input
                type="url"
                value={form.link}
                onChange={(e) => setForm({...form, link: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
                placeholder="https://..."
                required={form.content_type === 'external'}
              />
            </div>
          </>
        ) : (
          <div className="md:col-span-2">
            <label className="block text-gray-700 font-semibold mb-2">Video URL (YouTube)</label>
            <input
              type="url"
              value={form.video_url}
              onChange={(e) => setForm({...form, video_url: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {videoId && (
              <div className="mt-3">
                <p className="text-sm text-gray-500 mb-2">Preview:</p>
                <div className="aspect-video w-64 bg-black rounded overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video preview"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Featured Image */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label="Featured Image"
        />
      </div>
      
      {/* Photo Gallery */}
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Photo Gallery</label>
        <p className="text-sm text-gray-500 mb-3">Add multiple photos to display as a gallery in this news post</p>
        
        {/* Gallery Upload Button */}
        <label className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 rounded cursor-pointer hover:bg-navy-light transition-colors mb-3">
          <Camera size={18} />
          {galleryUploading ? 'Uploading...' : 'Add Photos'}
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            disabled={galleryUploading}
            onChange={async (e) => {
              const files = Array.from(e.target.files);
              if (files.length === 0) return;
              
              setGalleryUploading(true);
              const newUrls = [];
              
              for (const file of files) {
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  const res = await axios.post(`${API}/admin/upload`, formData, {
                    ...authConfig,
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
                  newUrls.push(res.data.url);
                } catch (err) {
                  console.error('Upload failed:', err);
                }
              }
              
              setForm({...form, gallery: [...form.gallery, ...newUrls]});
              setGalleryUploading(false);
            }}
          />
        </label>
        
        {/* Gallery Preview */}
        {form.gallery.length > 0 && (
          <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mt-3">
            {form.gallery.map((url, index) => (
              <div key={`gallery-${url}-${index}`} className="relative group">
                <img 
                  src={resolveImageUrl(url)} 
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-20 object-cover rounded border"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newGallery = form.gallery.filter((_, i) => i !== index);
                    setForm({...form, gallery: newGallery});
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        {form.gallery.length > 0 && (
          <p className="text-sm text-gray-500 mt-2">{form.gallery.length} photo{form.gallery.length !== 1 ? 's' : ''} in gallery</p>
        )}
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">
          {form.content_type === 'external' ? 'Summary *' : 'Summary / Excerpt *'}
        </label>
        <textarea
          value={form.summary}
          onChange={(e) => setForm({...form, summary: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
          rows="3"
          required
          placeholder={form.content_type === 'external' ? 'Brief summary of the article...' : 'Short excerpt shown in the news list...'}
        />
      </div>
      
      {/* Full Content for Original Posts */}
      {form.content_type === 'original' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Full Content</label>
          <RichTextEditor
            value={form.content}
            onChange={(content) => setForm({...form, content})}
          />
          <p className="text-sm text-gray-500 mt-1">Use the toolbar above to format your content</p>
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-navy text-white px-6 py-2 font-semibold hover:bg-navy-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Image Upload Component
const ImageUpload = ({ value, onChange, authConfig, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(resolveImageUrl(value) || '');
  const fileInputRef = React.useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/upload`, formData, {
        ...authConfig,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store just the relative path - resolveImageUrl will add the correct domain
      const uploadedUrl = res.data.url;
      onChange(uploadedUrl);
      setPreview(resolveImageUrl(uploadedUrl));
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload image. Please try again.');
      setPreview(value || '');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange(url);
    setPreview(url);
  };

  const handleRemove = () => {
    onChange('');
    setPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-gray-700 font-semibold mb-2">{label}</label>
      
      {/* Preview */}
      {preview && (
        <div className="mb-3 relative inline-block">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-24 h-24 object-contain border rounded bg-gray-50"
          />
          <button 
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
          >
            ×
          </button>
        </div>
      )}
      
      <div className="flex gap-2 items-center">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-${label.replace(/\s/g, '-')}`}
        />
        <label 
          htmlFor={`file-upload-${label.replace(/\s/g, '-')}`}
          className={`cursor-pointer bg-gray-100 border border-gray-300 px-4 py-2 rounded text-sm font-semibold hover:bg-gray-200 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </label>
        <span className="text-gray-400 text-sm">or</span>
        <input
          type="text"
          placeholder="Paste image URL"
          value={preview.startsWith('data:') ? '' : (preview || '')}
          onChange={handleUrlChange}
          className="flex-1 border border-gray-300 px-3 py-2 rounded text-sm focus:outline-none focus:border-navy"
        />
      </div>
    </div>
  );
};

// Gallery Form (with bulk upload support for new photos)
const GalleryForm = ({ photo, onSave, onCancel, authConfig, onBulkSave }) => {
  const [form, setForm] = useState({
    url: photo?.url || '',
    caption: photo?.caption || '',
    order: photo?.order || 0,
    is_active: photo?.is_active ?? true
  });
  
  // Bulk upload state
  const [bulkMode, setBulkMode] = useState(!photo); // Default to bulk mode for new photos
  const [bulkFiles, setBulkFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const dropZoneRef = React.useRef(null);
  const bulkInputRef = React.useRef(null);
  
  // Cropping state
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperIndex, setCropperIndex] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.url) {
      alert('Please upload or enter an image URL');
      return;
    }
    onSave(form);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Open cropper for a specific image
  const openCropper = (index) => {
    const file = bulkFiles[index];
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImage(reader.result);
      setCropperIndex(index);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle crop complete
  const handleCropComplete = (croppedBlob) => {
    // Create a new file from the cropped blob
    const croppedFile = new File([croppedBlob], bulkFiles[cropperIndex].name, { type: 'image/jpeg' });
    
    // Replace the file in bulkFiles
    setBulkFiles(prev => {
      const newFiles = [...prev];
      newFiles[cropperIndex] = croppedFile;
      return newFiles;
    });
    
    // Close cropper
    setCropperImage(null);
    setCropperIndex(null);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress({ current: 0, total: bulkFiles.length });
    
    const uploadedPhotos = [];
    
    for (let i = 0; i < bulkFiles.length; i++) {
      const file = bulkFiles[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/admin/upload`, formData, {
          ...authConfig,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        // Store just the relative path
        const uploadedUrl = res.data.url;
        uploadedPhotos.push({
          url: uploadedUrl,
          caption: '',
          order: i,
          is_active: true
        });
        
        setUploadProgress({ current: i + 1, total: bulkFiles.length });
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }
    
    setUploading(false);
    
    if (uploadedPhotos.length > 0 && onBulkSave) {
      await onBulkSave(uploadedPhotos);
    }
    
    setBulkFiles([]);
    onCancel();
  };

  // If editing existing photo, show single edit form
  if (photo) {
    return (
      <form onSubmit={handleSubmit}>
        <h3 className="font-heading text-xl text-navy uppercase mb-4">Edit Photo</h3>
        
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Current Photo</label>
          <img 
            src={resolveImageUrl(form.url)} 
            alt="Current" 
            className="w-32 h-32 object-cover rounded border"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Caption</label>
            <input
              type="text"
              value={form.caption}
              onChange={(e) => setForm({...form, caption: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              placeholder="Describe this photo..."
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              min="0"
            />
          </div>
        </div>
        
        <div className="mb-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
        
        <div className="flex gap-4">
          <button type="submit" className="bg-navy text-white px-6 py-2 font-semibold hover:bg-navy-light transition-colors">
            Save
          </button>
          <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    );
  }

  // Bulk upload form for new photos
  return (
    <div>
      <h3 className="font-heading text-xl text-navy uppercase mb-4">Add Photos (up to 30)</h3>
      
      {/* Drop zone */}
      <div
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-navy transition-colors cursor-pointer mb-4"
        onClick={() => bulkInputRef.current?.click()}
      >
        <input
          type="file"
          ref={bulkInputRef}
          accept="image/*"
          multiple
          onChange={handleBulkFileSelect}
          className="hidden"
        />
        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-semibold mb-1">Drop photos here or click to select</p>
        <p className="text-gray-400 text-sm">Upload up to 30 photos at once. Add captions later.</p>
      </div>
      
      {/* Preview of selected files */}
      {bulkFiles.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-700 font-semibold">{bulkFiles.length} photo(s) selected</p>
            <p className="text-sm text-gray-500">Click <Crop size={14} className="inline" /> to crop any photo</p>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-64 overflow-y-auto p-1">
            {bulkFiles.map((file, index) => (
              <div key={`bulk-${file.name}-${index}`} className="relative group">
                <img 
                  src={URL.createObjectURL(file)} 
                  alt={`Preview ${index + 1}`}
                  className="w-full aspect-square object-cover rounded border-2 border-transparent group-hover:border-navy transition-colors"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => openCropper(index)}
                    className="bg-white text-navy p-1.5 rounded hover:bg-gray-100"
                    title="Crop image"
                  >
                    <Crop size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeBulkFile(index)}
                    className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Image Cropper Modal */}
      {cropperImage && (
        <ImageCropperModal
          imageSrc={cropperImage}
          onCropComplete={handleCropComplete}
          onCancel={() => { setCropperImage(null); setCropperIndex(null); }}
          aspectRatio={1}
        />
      )}
      
      {/* Upload progress */}
      {uploading && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="animate-spin w-4 h-4 border-2 border-navy border-t-transparent rounded-full"></div>
            <span className="text-gray-600">Uploading {uploadProgress.current} of {uploadProgress.total}...</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-navy h-2 rounded-full transition-all duration-300"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <button 
          type="button"
          onClick={handleBulkUpload}
          disabled={bulkFiles.length === 0 || uploading}
          className="bg-campaign-red text-white px-6 py-2 font-semibold hover:bg-campaign-red-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : `Upload ${bulkFiles.length} Photo${bulkFiles.length !== 1 ? 's' : ''}`}
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// Endorsement Form
const EndorsementForm = ({ endorsement, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    name: endorsement?.name || '',
    title: endorsement?.title || '',
    organization: endorsement?.organization || '',
    quote: endorsement?.quote || '',
    image_url: endorsement?.image_url || '',
    endorsement_type: endorsement?.endorsement_type || 'individual',
    is_active: endorsement?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-navy uppercase mb-4">{endorsement ? 'Edit Endorsement' : 'Add Endorsement'}</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Endorsement Type *</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="organization"
              checked={form.endorsement_type === 'organization'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><Building2 size={16} /> Organization</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="individual"
              checked={form.endorsement_type === 'individual'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><User size={16} /> Individual</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Organization Name *' : 'Name *'}
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Yonkers Republican Committee' : 'e.g., Mike Spano'}
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Tagline/Description' : 'Title'}
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Serving Yonkers since 1920' : 'e.g., Mayor of Yonkers'}
          />
        </div>
        {form.endorsement_type === 'individual' && (
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Organization</label>
            <input
              type="text"
              value={form.organization}
              onChange={(e) => setForm({...form, organization: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              placeholder="e.g., Yonkers City Council"
            />
          </div>
        )}
      </div>
      
      {/* Image Upload */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label={form.endorsement_type === 'organization' ? 'Organization Logo' : 'Photo'}
        />
      </div>
      
      {form.endorsement_type === 'individual' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Quote</label>
          <textarea
            value={form.quote}
            onChange={(e) => setForm({...form, quote: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            rows="3"
            placeholder="Optional endorsement quote..."
          />
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-navy text-white px-6 py-2 font-semibold hover:bg-navy-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Issue Form
const IssueForm = ({ issue, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: issue?.title || '',
    icon: issue?.icon || 'Shield',
    description: issue?.description || '',
    details: issue?.details || [''],
    order: issue?.order || 0,
    is_active: issue?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Filter out empty details
    const cleanedDetails = form.details.filter(d => d.trim() !== '');
    onSave({ ...form, details: cleanedDetails });
  };

  const addDetail = () => {
    setForm({ ...form, details: [...form.details, ''] });
  };

  const removeDetail = (index) => {
    setForm({ ...form, details: form.details.filter((_, i) => i !== index) });
  };

  const updateDetail = (index, value) => {
    const newDetails = [...form.details];
    newDetails[index] = value;
    setForm({ ...form, details: newDetails });
  };

  const iconOptions = Object.keys(ICON_MAP);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-navy uppercase mb-4">{issue ? 'Edit Issue' : 'Add Issue'}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2">
          <label className="block text-gray-700 font-semibold mb-2">Issue Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            required
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            placeholder="e.g., Fix Public Safety"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Icon</label>
          <select
            value={form.icon}
            onChange={(e) => setForm({...form, icon: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          required
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
          rows="2"
          placeholder="Brief description shown on the card..."
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Key Points</label>
        {form.details.map((detail, index) => (
          <div key={`detail-${index}-${detail.substring(0, 10)}`} className="flex gap-2 mb-2">
            <input
              type="text"
              value={detail}
              onChange={(e) => updateDetail(index, e.target.value)}
              className="flex-1 border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
              placeholder={`Point ${index + 1}...`}
            />
            <button
              type="button"
              onClick={() => removeDetail(index)}
              className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addDetail}
          className="text-navy text-sm font-semibold hover:underline"
        >
          + Add another point
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-navy"
            min="0"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-navy text-white px-6 py-2 font-semibold hover:bg-navy-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Admin Page Wrapper
const AdminPage = () => {
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogin = (credentials) => {
    setAuth(credentials);
    localStorage.setItem('adminAuth', JSON.stringify(credentials));
  };

  const handleLogout = () => {
    setAuth(null);
    localStorage.removeItem('adminAuth');
  };

  // Validate saved credentials on mount
  useEffect(() => {
    const validateAuth = async () => {
      const savedAuth = localStorage.getItem('adminAuth');
      if (savedAuth) {
        try {
          const credentials = JSON.parse(savedAuth);
          // Verify credentials are still valid using POST body (not HTTP Basic Auth)
          const response = await axios.post(`${API}/admin/login`, {
            username: credentials.username,
            password: credentials.password
          }, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (response.data.authenticated) {
            setAuth(credentials);
          } else {
            localStorage.removeItem('adminAuth');
          }
        } catch (e) {
          // Credentials invalid, clear them
          localStorage.removeItem('adminAuth');
        }
      }
      setLoading(false);
    };
    validateAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!auth) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard auth={auth} onLogout={handleLogout} onAuthError={handleLogout} />;
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  usePageTitle("Privacy Policy");
  
  return (
    <div className="pt-8">
      <section className="py-16 bg-white">
        <div className="container-campaign">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-8">Privacy Policy</h1>
            <p className="text-gray-500 mb-8">Privacy Policy for Friends of John Isaac<br />Valid from: March 2025</p>
            
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-6">
                The team at Friends of John Isaac ("we," "our," or "us") is dedicated to preserving the privacy of our website's visitors and users ("you"). This Privacy Policy elucidates our methods for gathering, using, and disclosing your personal data on our website.
              </p>
              
              <p className="mb-6 font-semibold">
                By visiting and interacting with our website, you're agreeing to the terms of this Privacy Policy.
              </p>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">1. Information Collection</h2>
              <ul className="list-disc pl-6 mb-6 space-y-3">
                <li><strong>Personal Details:</strong> We might collect personal details that you willingly share with us, like your name, email ID, mailing address, contact number, and any other data you provide via our website's forms.</li>
                <li><strong>Text Message Opt-In Information:</strong> If you decide to receive text messages from us, we might gather your phone number and necessary data for the text messaging services.</li>
                <li><strong>Information Collected Automatically:</strong> We might automatically collect specific details about your device, browsing data, and usage habits when you visit our website. Such information could include IP addresses, cookies, and other tracking tools.</li>
              </ul>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">2. Information Sharing</h2>
              <ul className="list-disc pl-6 mb-6 space-y-3">
                <li>We will not distribute, sell, rent, or expose your personal data to any third parties, except as stipulated in this Privacy Policy or when necessitated by law.</li>
                <li><strong>Text Message Opt-In Information:</strong> We will not share or sell your text messaging opt-in data, consent, or associated personal information with any third parties, unless mandated by law.</li>
              </ul>
              
              <div className="bg-campaign-red/10 border-l-4 border-campaign-red p-6 my-8">
                <p className="text-navy font-semibold">
                  Text message opt-in consent and data will only be used for campaign-related communications and will not be shared or sold to third parties, except as required by law.
                </p>
              </div>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">3. Utilization of Information</h2>
              <p className="mb-4">We might use the personal details you provide for various purposes such as:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li>Communicating with you, responding to your queries, and offering information about our campaign.</li>
                <li>Sending you campaign updates, newsletters, and other relevant details.</li>
                <li>Analyzing and enhancing our website's functionality, content, and user experience.</li>
                <li>Fulfilling legal obligations and defending our rights and agreements.</li>
              </ul>
              <p className="mb-6">
                <strong>Text Message Opt-In Information:</strong> Your phone number and related details collected for text messaging services will solely be used to deliver you campaign-related text messages and updates.
              </p>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">4. Data Protection</h2>
              <p className="mb-6">
                We employ reasonable security measures and industry-standard protection technologies to safeguard your personal information. However, transmitting data over the internet or storing it electronically is not entirely secure, and we cannot promise complete security.
              </p>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">5. Third-Party Services</h2>
              <p className="mb-6">
                Our website may provide links to third-party websites or services. We hold no responsibility for the privacy practices or content of such third parties. We recommend that you review their privacy policies when visiting their websites or using their services.
              </p>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">6. Children's Privacy</h2>
              <p className="mb-6">
                Our website isn't designed for use by individuals below 13 years of age. We don't intentionally collect personal data from children under 13. If we discover that we've inadvertently gathered personal data from a child under 13 without parental consent, we'll act promptly to remove such information from our records.
              </p>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">7. Amendments to this Privacy Policy</h2>
              <p className="mb-6">
                We might revise this Privacy Policy occasionally to reflect changes in our methods or for other operational, legal, or regulatory purposes. Any amendments will be immediately effective upon the updated Privacy Policy's posting on our website. We encourage you to check this page periodically to stay updated about our privacy practices.
              </p>
              
              <h2 className="font-heading text-2xl text-navy uppercase mt-10 mb-4">8. Get in Touch</h2>
              <p className="mb-6">
                If you have any queries or concerns about this Privacy Policy or our privacy practices, feel free to contact us at:
              </p>
              <div className="bg-gray-50 p-6 rounded-sm">
                <p className="text-navy font-semibold">Friends of John Isaac</p>
                <p className="text-gray-600">{CAMPAIGN_DATA.contact.address}</p>
                <p className="text-gray-600">{CAMPAIGN_DATA.contact.website}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Individual News Article Page
const NewsArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${API}/news/${id}`);
        setArticle(res.data);
        document.title = `${res.data.title} | John Isaac for NY State Assembly`;
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);
  
  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }
  
  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-navy uppercase mb-4">Article Not Found</h1>
          <Link to="/news" className="text-campaign-red hover:underline">← Back to News</Link>
        </div>
      </div>
    );
  }
  
  const videoId = getYouTubeId(article.video_url);
  
  return (
    <div className="pt-8">
      <article className="py-12 bg-white">
        <div className="container-campaign">
          <div className="max-w-4xl mx-auto">
            {/* Back link */}
            <Link to="/news" className="text-campaign-red hover:underline inline-flex items-center gap-1 mb-8">
              <ChevronLeft size={18} /> Back to News
            </Link>
            
            {/* Article Header */}
            <header className="mb-8">
              <p className="text-gray-500 mb-2">{article.published_date}</p>
              <h1 className="font-heading text-4xl md:text-5xl text-navy uppercase mb-4">
                {article.title}
              </h1>
              <p className="text-xl text-gray-600 font-body">{article.summary}</p>
            </header>
            
            {/* Video embed */}
            {videoId && (
              <div className="aspect-video w-full bg-black rounded-sm overflow-hidden mb-8">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={article.title}
                />
              </div>
            )}
            
            {/* Featured Image (if no video) */}
            {article.image_url && !videoId && (
              <img 
                src={resolveImageUrl(article.image_url)} 
                alt={article.title}
                className="w-full max-h-[500px] object-cover rounded-sm mb-8"
              />
            )}
            
            {/* Article Content */}
            {article.content && (
              <div className="prose prose-lg max-w-none mb-12">
                <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.content) }} />
              </div>
            )}
            
            {/* Photo Gallery */}
            {article.gallery && article.gallery.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <h2 className="font-heading text-2xl text-navy uppercase mb-6">Photo Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {article.gallery.map((url, index) => (
                    <button 
                      key={`article-gallery-${url}-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className="block overflow-hidden rounded group aspect-square"
                    >
                      <img 
                        src={resolveImageUrl(url)} 
                        alt={`Gallery photo ${index + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Share buttons */}
            <div className="mt-12 pt-8 border-t">
              <p className="font-heading text-lg text-navy uppercase mb-4">Share This Article</p>
              <div className="flex gap-4">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#1877F2] text-white px-6 py-2 rounded flex items-center gap-2 hover:opacity-90"
                >
                  <Facebook size={18} /> Share
                </a>
              </div>
            </div>
            
            {/* Back to News */}
            <div className="mt-12 text-center">
              <Link to="/news" className="btn-secondary inline-flex items-center gap-2">
                <ChevronLeft size={18} /> View All News
              </Link>
            </div>
          </div>
        </div>
      </article>
      
      {/* Lightbox for gallery images */}
      {selectedImage !== null && article.gallery && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <X size={32} />
          </button>
          
          {/* Previous button */}
          {selectedImage > 0 && (
            <button 
              onClick={(e) => { e.stopPropagation(); setSelectedImage(selectedImage - 1); }}
              className="absolute left-4 text-white hover:text-gray-300"
            >
              <ChevronLeft size={48} />
            </button>
          )}
          
          {/* Image */}
          <img 
            src={resolveImageUrl(article.gallery[selectedImage])} 
            alt={`Gallery photo ${selectedImage + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Next button */}
          {selectedImage < article.gallery.length - 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); setSelectedImage(selectedImage + 1); }}
              className="absolute right-4 text-white hover:text-gray-300"
            >
              <ChevronRight size={48} />
            </button>
          )}
          
          {/* Counter */}
          <div className="absolute bottom-4 text-white text-sm">
            {selectedImage + 1} / {article.gallery.length}
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Layout
const MainLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<MainLayout><Home /></MainLayout>} />
          <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
          <Route path="/events" element={<MainLayout><EventsPage /></MainLayout>} />
          <Route path="/events/:eventId" element={<MainLayout><EventDetailPage /></MainLayout>} />
          <Route path="/news" element={<MainLayout><NewsPage /></MainLayout>} />
          <Route path="/news/:id" element={<MainLayout><NewsArticlePage /></MainLayout>} />
          <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
